import { defineComponent as _defineComponent } from 'vue'
import { renderSlot as _renderSlot, createElementVNode as _createElementVNode, normalizeClass as _normalizeClass, normalizeStyle as _normalizeStyle, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, Teleport as _Teleport, createBlock as _createBlock } from "vue"

const _hoisted_1 = { class: "app-tooltip" }
const _hoisted_2 = ["innerHTML"]

import {
	ref,
	computed,
	watch,
	onMounted,
	onUnmounted,
	nextTick,
	type StyleValue
} from 'vue'
import { useBreakpoints } from '@/composables/useBreakpoints'

type AppTooltipProps = {
	position?: 'top' | 'bottom' | 'left' | 'right'
	showArrow?: boolean
	text: string
	maxWidth?: string
	showByActivatorClick?: boolean,
	closeByClickDelay?: number,
	delay?: number
}


export default /*@__PURE__*/_defineComponent({
  __name: 'AppTooltip',
  props: {
    position: { default: 'top' },
    showArrow: { type: Boolean, default: false },
    text: {},
    maxWidth: { default: '300px' },
    showByActivatorClick: { type: Boolean, default: false },
    closeByClickDelay: { default: 3000 },
    delay: { default: 1000 }
  },
  setup(__props: any, { expose: __expose }) {

const props = __props

const { isMobile, isTablet } = useBreakpoints()

const isVisible = ref(false)
const tooltipRef = ref<HTMLElement | null>(null)
const activatorRef = ref<HTMLElement | null>(null)

const computedPosition = ref(props.position)

const tooltipPosition = ref({ top: 0, left: 0 })
const arrowOffset = ref(0)
const delayTimer = ref<ReturnType<typeof setTimeout> | null>(null)

const showTooltip = () => {
	if (isMobile.value || isTablet.value) return
	if (delayTimer.value) clearTimeout(delayTimer.value)

	delayTimer.value = setTimeout(async () => {
		isVisible.value = true
		await nextTick()
		updatePosition()
		delayTimer.value = null
	}, props.delay)
}

const hideTooltip = () => {
	if (delayTimer.value) {
		clearTimeout(delayTimer.value)
		delayTimer.value = null
	}

	isVisible.value = false
}

const showByClick = async () => {
	if (isMobile.value || isTablet.value) return
	isVisible.value = true
	await nextTick()
	updatePosition()
	setTimeout(() => hideTooltip(), props.closeByClickDelay)
}

// Проверить, помещается ли тултип с указанной стороны
const checkPositionFit = (
	position: 'top' | 'bottom' | 'left' | 'right',
	top: number,
	left: number,
	tooltipRect: DOMRect,
	activatorRect: DOMRect
): boolean => {
	const viewportWidth = window.innerWidth
	const viewportHeight = window.innerHeight
	const minMargin = 10 // минимальный отступ от края экрана

	switch (position) {
		case 'top':
			return top >= minMargin
		case 'bottom':
			return top + tooltipRect.height <= viewportHeight - minMargin
		case 'left':
			return left >= minMargin
		case 'right':
			return left + tooltipRect.width <= viewportWidth - minMargin
		default:
			return true
	}
}

// Получить противоположную позицию
const getOppositePosition = (position: 'top' | 'bottom' | 'left' | 'right'): 'top' | 'bottom' | 'left' | 'right' => {
	switch (position) {
		case 'top': return 'bottom'
		case 'bottom': return 'top'
		case 'left': return 'right'
		case 'right': return 'left'
	}
}

// Обновить позицию тултипа
const updatePosition = () => {
	if (!activatorRef.value || !tooltipRef.value) return

	const activatorRect = activatorRef.value.getBoundingClientRect()
	const tooltipRect = tooltipRef.value.getBoundingClientRect()
	const scrollY = window.pageYOffset || document.documentElement.scrollTop
	const scrollX = window.pageXOffset || document.documentElement.scrollLeft

	// Пытаемся разместить тултип в запрошенной позиции
	let position = props.position
	let top = 0
	let left = 0
	const spacing = 8 // расстояние между активатором и тултипом

	// Рассчитываем идеальную позицию (по центру)
	switch (position) {
		case 'top':
			top = activatorRect.top + scrollY - tooltipRect.height - spacing
			left = activatorRect.left + scrollX + activatorRect.width / 2 - tooltipRect.width / 2
			break
		case 'bottom':
			top = activatorRect.bottom + scrollY + spacing
			left = activatorRect.left + scrollX + activatorRect.width / 2 - tooltipRect.width / 2
			break
		case 'left':
			top = activatorRect.top + scrollY + activatorRect.height / 2 - tooltipRect.height / 2
			left = activatorRect.left + scrollX - tooltipRect.width - spacing
			break
		case 'right':
			top = activatorRect.top + scrollY + activatorRect.height / 2 - tooltipRect.height / 2
			left = activatorRect.right + scrollX + spacing
			break
	}

	// Проверяем, помещается ли тултип в этой позиции
	const fitsOriginalPosition = checkPositionFit(
		position,
		top,
		left,
		tooltipRect,
		activatorRect
	)

	// Если не помещается, используем противоположную позицию
	if (!fitsOriginalPosition) {
		position = getOppositePosition(position)

		// Пересчитываем для противоположной позиции
		switch (position) {
			case 'top':
				top = activatorRect.top + scrollY - tooltipRect.height - spacing
				left = activatorRect.left + scrollX + activatorRect.width / 2 - tooltipRect.width / 2
				break
			case 'bottom':
				top = activatorRect.bottom + scrollY + spacing
				left = activatorRect.left + scrollX + activatorRect.width / 2 - tooltipRect.width / 2
				break
			case 'left':
				top = activatorRect.top + scrollY + activatorRect.height / 2 - tooltipRect.height / 2
				left = activatorRect.left + scrollX - tooltipRect.width - spacing
				break
			case 'right':
				top = activatorRect.top + scrollY + activatorRect.height / 2 - tooltipRect.height / 2
				left = activatorRect.right + scrollX + spacing
				break
		}
	}

	// Обновляем вычисленную позицию для отображения правильной стрелки
	computedPosition.value = position

	// Корректировка для предотвращения выхода за границы экрана
	const viewportWidth = window.innerWidth
	const viewportHeight = window.innerHeight
	const minMargin = 10

	// Сохраняем идеальную позицию для расчета смещения стрелки
	const idealLeft = left
	const idealTop = top

	// Корректировка по горизонтали
	if (left < minMargin) left = minMargin
	if (left + tooltipRect.width > viewportWidth - minMargin) {
		left = viewportWidth - tooltipRect.width - minMargin
	}

	// Корректировка по вертикали (если все равно не помещается даже в противоположной позиции)
	if (top < minMargin) top = minMargin
	if (top + tooltipRect.height > viewportHeight - minMargin) {
		top = viewportHeight - tooltipRect.height - minMargin
	}

	// Рассчитываем смещение стрелки
	arrowOffset.value = 0

	if (computedPosition.value === 'top' || computedPosition.value === 'bottom') {
		// Вычисляем центр активатора относительно тултипа
		const activatorCenterX = activatorRect.left + scrollX + activatorRect.width / 2
		const tooltipCenterX = left + tooltipRect.width / 2

		// Идеальное положение стрелки - по центру тултипа (50%)
		// Но если тултип смещен, нужно сместить стрелку относительно центра
		const idealArrowPosition = 50 // 50% от ширины тултипа

		// Вычисляем, насколько сместился тултип относительно идеальной позиции
		const tooltipShiftX = left - idealLeft

		// Вычисляем новую позицию стрелки
		let arrowPosition = idealArrowPosition - (tooltipShiftX / tooltipRect.width * 100)

		// Ограничиваем позицию стрелки, чтобы она не выходила за пределы тултипа
		const minArrowPosition = 15 // минимальный отступ от края тултипа
		const maxArrowPosition = 85 // максимальный отступ от края тултипа

		arrowPosition = Math.max(minArrowPosition, Math.min(maxArrowPosition, arrowPosition))

		arrowOffset.value = arrowPosition - 50 // сохраняем смещение от центра

	} else if (computedPosition.value === 'left' || computedPosition.value === 'right') {
		const activatorCenterY = activatorRect.top + scrollY + activatorRect.height / 2
		const tooltipCenterY = top + tooltipRect.height / 2

		const idealArrowPosition = 50 // 50% от высоты тултипа
		const tooltipShiftY = top - idealTop

		let arrowPosition = idealArrowPosition - (tooltipShiftY / tooltipRect.height * 100)

		const minArrowPosition = 15
		const maxArrowPosition = 85

		arrowPosition = Math.max(minArrowPosition, Math.min(maxArrowPosition, arrowPosition))

		arrowOffset.value = arrowPosition - 50
	}

	tooltipPosition.value = { top, left }
}

const tooltipStyles = computed<StyleValue>(() => ({
	'max-width': props.maxWidth,
	top: `${tooltipPosition.value.top}px`,
	left: `${tooltipPosition.value.left}px`,
}))

const arrowStyles = computed<StyleValue>(() => {
	const styles: any = {}

	if (computedPosition.value === 'top' || computedPosition.value === 'bottom') {
		styles.left = `calc(50% + ${arrowOffset.value}%)`
	} else if (computedPosition.value === 'left' || computedPosition.value === 'right') {
		styles.top = `calc(50% + ${arrowOffset.value}%)`
	}

	return styles
})

const handleResize = () => {
	if (isVisible.value) {
		updatePosition()
	}
}

const handleScroll = () => {
	if (isVisible.value) {
		updatePosition()
	}
}

watch(isVisible, (newValue) => {
	if (newValue) {
		window.addEventListener('resize', handleResize)
		window.addEventListener('scroll', handleScroll, true)
		updatePosition()
	} else {
		window.removeEventListener('resize', handleResize)
		window.removeEventListener('scroll', handleScroll, true)
	}
})

onUnmounted(() => {
	if (delayTimer.value) clearTimeout(delayTimer.value)

	window.removeEventListener('resize', handleResize)
	window.removeEventListener('scroll', handleScroll, true)
})

onMounted(() => {
	if (isVisible.value) {
		updatePosition()
	}
})

__expose({ showByClick })

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    _createElementVNode("div", {
      class: "app-tooltip__activator",
      ref_key: "activatorRef",
      ref: activatorRef,
      onMouseenter: _cache[0] || (_cache[0] = ($event: any) => (!props.showByActivatorClick ? showTooltip() : null)),
      onMouseleave: _cache[1] || (_cache[1] = ($event: any) => (!props.showByActivatorClick ? hideTooltip() : null))
    }, [
      _renderSlot(_ctx.$slots, "activator")
    ], 544),
    (_openBlock(), _createBlock(_Teleport, { to: "body" }, [
      (isVisible.value)
        ? (_openBlock(), _createElementBlock("div", {
            key: 0,
            ref_key: "tooltipRef",
            ref: tooltipRef,
            class: _normalizeClass(["app-tooltip__tooltip", [
					`app-tooltip__tooltip--${computedPosition.value}`,
					{ 'app-tooltip__tooltip--with-arrow': _ctx.showArrow }
				]]),
            style: _normalizeStyle(tooltipStyles.value)
          }, [
            _createElementVNode("div", {
              class: "app-tooltip__tooltip-content",
              innerHTML: _ctx.text
            }, null, 8, _hoisted_2),
            (_ctx.showArrow)
              ? (_openBlock(), _createElementBlock("div", {
                  key: 0,
                  class: _normalizeClass(["app-tooltip__tooltip-arrow", `app-tooltip__tooltip-arrow--${computedPosition.value}`]),
                  style: _normalizeStyle(arrowStyles.value)
                }, null, 6))
              : _createCommentVNode("", true)
          ], 6))
        : _createCommentVNode("", true)
    ]))
  ]))
}
}

})